import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';

import {
	fireErrorAnalytics,
	fireMsrNotRecommendedAnalytics,
	fireSiteOptedOutAnalytics,
} from '../../services/analytics/index.tsx';
import { getOrUpdateFeatureVisited, useSnooze } from '../../services/caching/index.tsx';
import { hasProductAccess } from '../../services/permission-check/index.tsx';
import { useFetchIsRequestAccessCapable } from '../../services/request-capabilities/index.tsx';
import { isSite7DaysOld } from '../../services/site-eligibility/index.tsx';
import { isSiteOptedOut } from '../../services/site-opt-out/index.tsx';
import { isNewActiveUser } from '../../services/user-eligibility/index.tsx';
import { useNudgeStateJiraToJpd } from '../../services/nudge-state/index.tsx';
import { MAX_FEATURE_VISIT_COUNT } from '../nudges/common.tsx';
import type { EligibilityCheck } from './types.tsx';
import { isExcludedForNav4Placement } from '../../services/cjn-nav4-placement/isExcludedForNav4Placement.tsx';

type UseJpdCrossJoinNudgeEligibilityProps = {
	getMSRNudgeRecommendations: () => Promise<string[]>;
};

export const useJpdCrossJoinNudgeEligibility = ({
	getMSRNudgeRecommendations,
}: UseJpdCrossJoinNudgeEligibilityProps): (() => Promise<
	EligibilityCheck<{ featureVisitedCount: number }>
>) => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const licensedProducts = useLicensedProducts();
	const isJpdRoute = useIsActiveRoute('polaris-ideas');
	const fetchIsRequestAccessCapable = useFetchIsRequestAccessCapable('crossJoinNudgesJiraToJpd');

	const { getVisitedCount } = useSnooze('crossJoinNudgesJiraToJpd');
	const { getNudgeState } = useNudgeStateJiraToJpd();

	return useCallback(async (): Promise<EligibilityCheck<{ featureVisitedCount: number }>> => {
		try {
			if (getOrUpdateFeatureVisited('crossJoinNudgesJiraToJpd', 7)) {
				return { isEligible: false, ineligibleReason: 'feature_visited_too_recently' };
			}

			if (isJpdRoute) {
				return { isEligible: false, ineligibleReason: 'jpd_route' };
			}

			const featureVisitedCount = await getVisitedCount();
			if (featureVisitedCount >= MAX_FEATURE_VISIT_COUNT) {
				return { isEligible: false, ineligibleReason: 'nudge_seen_too_many_times' };
			}

			if ((await getNudgeState()) === 'DISMISSED') {
				return { isEligible: false, ineligibleReason: 'nudge_already_dismissed' };
			}

			if (!licensedProducts['jira-product-discovery']) {
				return {
					isEligible: false,
					ineligibleReason: 'no_jpd_licence',
				};
			}

			if (await isSiteOptedOut(cloudId)) {
				fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesJiraToJpd');
				return {
					isEligible: false,
					ineligibleReason: 'site_is_opted_out',
				};
			}

			if (!(await isSite7DaysOld('jira-product-discovery', cloudId))) {
				return {
					isEligible: false,
					ineligibleReason: 'new_site_less_than_7_days_old',
				};
			}

			if (await isNewActiveUser(cloudId, userId)) {
				return {
					isEligible: false,
					ineligibleReason: 'new_active_user',
				};
			}

			if (await hasProductAccess('jira-product-discovery', cloudId)) {
				return {
					isEligible: false,
					ineligibleReason: 'already_have_access',
				};
			}

			if (!(await fetchIsRequestAccessCapable('jira-product-discovery', cloudId))) {
				return {
					isEligible: false,
					ineligibleReason: 'not_eligible_for_request_access',
				};
			}

			if (
				await isExcludedForNav4Placement(
					cloudId,
					userId,
					createAnalyticsEvent,
					'jira_productDiscovery',
				)
			) {
				return { isEligible: false, ineligibleReason: 'excluded_for_nav4_placement_experiment' };
			}

			const recommendations = await getMSRNudgeRecommendations();

			if (!recommendations.includes('cjn-jira-jpd')) {
				fireMsrNotRecommendedAnalytics(
					createAnalyticsEvent,
					'crossJoinNudgesJiraToJpd',
					'cjn-jira-jpd',
				);
				return { isEligible: false, ineligibleReason: 'msr_nudge_not_recommended' };
			}

			return { isEligible: true, data: { featureVisitedCount } };

			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('crossJoinNudgesJiraToJpd', error, { cloudId });
			return { isEligible: false, ineligibleReason: 'unexpected error' };
		}
	}, [
		isJpdRoute,
		getVisitedCount,
		getNudgeState,
		cloudId,
		userId,
		licensedProducts,
		createAnalyticsEvent,
		fetchIsRequestAccessCapable,
		getMSRNudgeRecommendations,
	]);
};
